/**
 * SEO content for individual job landing pages — 12 unique jobs, one per family.
 * Each entry powers /metiers/:slug with high-intent Google traffic and rich JSON-LD.
 *
 * Families (post legal-lock pivot Feb 2026):
 *   - Restauration/CHR : serveur, chef-de-rang, maitre-hotel, barman, cuisinier, commis, plongeur
 *   - Hôtellerie/Événementiel : hote-accueil
 *   - Logistique/Livraison : livreur, livreur-monteur, manutentionnaire
 *   - Entretien/Propreté : femme-de-menage
 *
 * IMPORTANT for indexing :
 *   - h1 ≤ 70 chars
 *   - meta_desc ≤ 158 chars
 *   - intro_paragraphs : 2-3 paragraphes uniques (no duplicate content across pages)
 *   - keywords : 4-6 mots-clés cibles (repris dans <meta keywords> + naturellement dans le body)
 *   - popular_cities : uniquement AURA (Lyon, Villeurbanne, Grenoble, Valence, périphéries)
 */

const AURA_CITIES = [
    "Lyon", "Villeurbanne", "Caluire-et-Cuire", "Vénissieux",
    "Grenoble", "Meylan", "Échirolles",
    "Valence", "Bourg-lès-Valence", "Romans-sur-Isère",
];

export const JOB_SEO = {
    // ================= RESTAURATION / CHR =================
    serveur: {
        slug: "serveur",
        category_key: "serveur",
        emoji: "🍽️",
        family: "Restauration & CHR",
        h1: "Serveur / Serveuse — missions restauration & événementiel en AURA",
        title: "Serveur restaurant & événementiel : missions 16-22 €/h | Renforteo",
        meta_desc:
            "Missions de serveur / serveuse à Lyon, Grenoble, Valence : brasserie, mariage, cocktail. 16-22 €/h en professionnel indépendant. Inscription 2 min sur Renforteo.",
        keywords: ["serveur Lyon", "emploi serveur Lyon", "intérim serveur Lyon", "serveur mariage", "serveur cocktail", "serveur événementiel"],
        hero_img: "https://images.pexels.com/photos/6544378/pexels-photo-6544378.jpeg?auto=compress&cs=tinysrgb&w=1400",
        intro_paragraphs: [
            "Renforteo met en relation restaurants, brasseries, hôtels et traiteurs de la région Auvergne-Rhône-Alpes avec des serveurs et serveuses professionnels indépendants vérifiés. Que vous soyez un bouchon lyonnais en coup de feu le samedi soir, un traiteur qui prépare un mariage à Valence ou une brasserie qui a besoin d'un renfort du midi à Grenoble, notre plateforme diffuse votre mission auprès des professionnels disponibles dans un rayon défini.",
            "Le métier de serveur combine sens du service, mémoire des commandes, rythme soutenu et connaissance basique des accords mets-vins (particulièrement à Lyon, capitale mondiale de la gastronomie). Les missions vont du service midi standard (bistrot de quartier) au grand cocktail dînatoire haut de gamme (Palais de la Bourse, Villa Maïa, Cité du Chocolat de Tain).",
            "Sur Renforteo, chaque serveur est immatriculé (SIRET vérifié), a fourni son KBIS ou sa fiche INSEE, sa carte d'identité recto/verso contrôlée par IA Gemini et son RIB. Les frais plateforme sont fixés à 8 % côté professionnel — bien plus bas que les 30-50 % des agences d'intérim classiques.",
        ],
        client_pitch:
            "Coup de feu, service manquant, mariage ou événement corporate ? Publiez votre besoin en 60 secondes et recevez des candidatures de serveurs pré-vérifiés dans l'heure en semaine.",
        candidate_pitch:
            "Professionnel indépendant ou en création de statut ? Trouvez des missions de service près de chez vous (Lyon, Villeurbanne, Grenoble, Valence…), choisissez vos créneaux, et facturez directement.",
        salary_range: "16 – 22 €/h",
        salary_details: {
            min: 16,
            avg: 18,
            max: 22,
            factors: [
                "Serveur standard (brunch, service midi, brasserie) : 16-18 €/h",
                "Serveur événementiel (mariage, cocktail, gala) : 18-20 €/h",
                "Chef de rang (restaurant gastronomique, dressage à la française) : 20-22 €/h",
                "Majorations : nuit +25 %, dimanche +50 %, jour férié +50 %, urgent +10 %",
            ],
        },
        popular_cities: AURA_CITIES,
        typical_missions: [
            "Service en salle midi et soir (bistrot, brasserie, restaurant gastronomique)",
            "Mariage / cocktail dînatoire dans un lieu de réception (Villa Maïa, Domaine Le Cep…)",
            "Brunch dominical récurrent en hôtel ou restaurant du dimanche",
            "Renfort banquet ou repas d'entreprise (200 à 500 couverts)",
            "Service événementiel corporate en salon (Sirha, Alpexpo, Pollutec)",
            "Extra week-end en salle de réception rurale ou viticole",
        ],
        prerequisites: [
            "Expérience minimum de 3 mois en service (mise en place, prise de commande)",
            "Présentation soignée (chemise blanche, pantalon noir, chaussures noires)",
            "Connaissance des règles d'hygiène et de sécurité alimentaire (HACCP recommandée)",
            "Ponctualité extrême et capacité à travailler en équipe sous pression",
            "SIRET professionnel indépendant + KBIS ou fiche INSEE + carte d'identité + RIB",
        ],
        job_context:
            "La demande de serveurs en AURA est portée par 3 dynamiques : (1) la scène gastronomique lyonnaise avec ses 4 000 restaurants et ses grands événements (Fête des Lumières, Sirha), (2) le tourisme d'affaires à Grenoble (séminaires tech CEA-Minatec, ouvertures de saison ski) et (3) l'événementiel privé haut de gamme à Valence (mariages viticoles Crozes-Hermitage, réceptions Anne-Sophie Pic).",
        pros_cons: {
            pros: [
                "Missions ponctuelles idéales en complément d'activité",
                "Facturation directe, pas d'intermédiaire d'intérim",
                "Choix libre des missions (aucune obligation d'accepter)",
                "Paiement Stripe sécurisé sous 7 jours après validation du timesheet",
            ],
            cons: [
                "Horaires souvent en soirée / week-end",
                "Cadence intense pendant le coup de feu",
                "Statut professionnel indépendant obligatoire (SIRET requis)",
            ],
        },
        faqs: [
            {
                q: "Combien gagne un serveur à Lyon en 2026 ?",
                a: "Entre 16 et 22 €/h HT facturés au client. Un serveur régulier peut générer 1 500 à 2 500 € net par mois en complément d'activité, sans intermédiaire d'intérim.",
            },
            {
                q: "Faut-il de l'expérience pour postuler comme serveur ?",
                a: "3 mois minimum d'expérience en service sont recommandés (mise en place, prise de commande, encaissement). Les clients notent chaque mission — les serveurs les mieux notés reçoivent le badge « Super Talent » qui débloque les missions premium.",
            },
            {
                q: "Renforteo est-il vraiment moins cher qu'une agence d'intérim ?",
                a: "Oui. Les agences d'intérim classiques prélèvent 30 à 50 % du montant facturé au client. Renforteo prélève 8 % côté professionnel + 15 % côté entreprise (inclus dans le prix TTC affiché). Vous conservez plusieurs euros supplémentaires par heure travaillée.",
            },
            {
                q: "Puis-je refuser une mission ?",
                a: "Bien sûr. Vous êtes indépendant. Aucune obligation d'accepter, aucune justification à donner. Renforteo vous propose des missions compatibles avec votre profil et votre rayon — vous décidez.",
            },
            {
                q: "Comment sont gérées les tenues de service ?",
                a: "Sauf mention contraire du client, le serveur apporte sa propre tenue de service (chemise blanche, pantalon noir, chaussures noires). Certains clients premium fournissent la tenue sur place — c'est précisé dans le brief de mission.",
            },
        ],
        related_slugs: ["chef-de-rang", "maitre-hotel", "barman", "commis"],
    },

    "chef-de-rang": {
        slug: "chef-de-rang",
        category_key: "chef_de_rang",
        emoji: "🍷",
        family: "Restauration & CHR",
        h1: "Chef de rang — missions restaurants étoilés & gastronomiques",
        title: "Chef de rang : missions restaurants étoilés 20-26 €/h | Renforteo",
        meta_desc:
            "Missions de chef de rang à Lyon, Grenoble, Valence : encadrement service, restaurants gastronomiques, dressage à la française. 20-26 €/h. Renforteo.",
        keywords: ["chef de rang Lyon", "chef de rang gastronomique", "encadrement service", "dressage français", "restaurant étoilé serveur"],
        hero_img: "https://images.pexels.com/photos/2696064/pexels-photo-2696064.jpeg?auto=compress&cs=tinysrgb&w=1400",
        intro_paragraphs: [
            "Le chef de rang orchestre une équipe de 2 à 4 commis / serveurs sur un rang de tables (typiquement 15 à 25 couverts). C'est le maillon central du service en restaurant gastronomique : il connaît la carte dans le détail, réalise le dressage à la française, découpe le pigeon, flambe la crêpe Suzette et conseille en accords mets-vins.",
            "En Auvergne-Rhône-Alpes, la demande de chefs de rang est particulièrement forte à Lyon (bouchons haut de gamme, restaurants 6ᵉ arr., Villa Maïa), à Valence (maison Anne-Sophie Pic et sa mouvance) et à Grenoble (hôtels 4-5* de la Presqu'île iséroise). Renforteo met en relation les établissements et les chefs de rang professionnels indépendants vérifiés, disponibles pour des extras réguliers ou des remplacements de dernière minute.",
        ],
        client_pitch:
            "Restaurant gastronomique en manque de chef de rang pour un service critique ? Recevez des candidatures de professionnels avec expérience étoilée en moins de 24 h.",
        candidate_pitch:
            "Vous êtes chef de rang confirmé (2+ ans d'expérience gastronomique) ? Accédez à des missions premium 20-26 €/h dans les maisons étoilées et hôtels 5* de la région.",
        salary_range: "20 – 26 €/h",
        salary_details: {
            min: 20,
            avg: 22,
            max: 26,
            factors: [
                "Restaurant bistronomique / hôtel 4* : 20-22 €/h",
                "Restaurant gastronomique / hôtel 5* : 22-24 €/h",
                "Maison étoilée Michelin / palace : 24-26 €/h",
                "Majorations : nuit +25 %, dimanche +50 %, jour férié +50 %",
            ],
        },
        popular_cities: AURA_CITIES,
        typical_missions: [
            "Encadrement d'un rang en restaurant gastronomique (dressage, découpe, flambage)",
            "Service midi / soir en hôtel 4-5* (chambres, room service événementiel)",
            "Grand banquet ou dîner de gala (200+ couverts) avec encadrement d'équipe",
            "Extra sur événement corporate premium (soirée bar-mitzvah, mariage haut de gamme)",
            "Remplacement d'un chef de rang en poste (arrêt maladie, congé maternité)",
        ],
        prerequisites: [
            "Expérience minimum 2 ans en gastronomie (CAP + BTS Serveur ou équivalent apprécié)",
            "Maîtrise du dressage à la française et des découpes classiques",
            "Solides connaissances en accords mets-vins (Vallée du Rhône, Beaujolais, Bourgogne)",
            "Aisance en anglais professionnel (clientèle internationale)",
            "SIRET professionnel indépendant + KBIS ou fiche INSEE + RIB",
        ],
        job_context:
            "En Auvergne-Rhône-Alpes, 15+ étoiles Michelin brillent (Pic à Valence, Bocuse à Collonges, Lameloise en Bourgogne toute proche, Chibois à Meylan). Ces maisons cherchent régulièrement des chefs de rang expérimentés pour couvrir arrêts, congés maternité et pics d'activité (Noël, Saint-Valentin, mariages d'été).",
        pros_cons: {
            pros: [
                "Tarifs les plus élevés de la famille restauration (jusqu'à 26 €/h)",
                "Accès à des maisons prestigieuses et à la culture gastronomique haut de gamme",
                "Missions valorisantes qui enrichissent votre CV professionnel",
            ],
            cons: [
                "Expérience minimum 2 ans requise, pas un métier d'entrée",
                "Standards très exigeants (tenue, gestuelle, mémoire de carte)",
                "Cadence intense pendant les services",
            ],
        },
        faqs: [
            {
                q: "Différence entre serveur et chef de rang ?",
                a: "Le serveur exécute (prise de commande, service à l'assiette, débarrassage). Le chef de rang encadre 2-4 commis, réalise les gestes techniques (découpe, flambage, dressage) et est l'interlocuteur privilégié de la table pour les conseils. Rémunération 4-6 €/h supérieure.",
            },
            {
                q: "Faut-il maîtriser l'anglais pour être chef de rang à Lyon ?",
                a: "Oui, particulièrement en hôtellerie 4-5* et restaurants gastronomiques. Lyon accueille une clientèle internationale (voyages d'affaires, tourisme de luxe). Un niveau B2 minimum est attendu. Le japonais et l'italien sont un plus.",
            },
            {
                q: "Puis-je alterner missions chef de rang et serveur ?",
                a: "Oui. Sur Renforteo, vous pouvez cocher les deux métiers dans votre profil. Le système vous proposera les deux types de missions selon la demande — utile en période creuse.",
            },
            {
                q: "Renforteo travaille-t-il avec des étoilés Michelin ?",
                a: "Oui, plusieurs maisons étoilées et Grands Chefs Relais & Châteaux de la région passent par Renforteo pour couvrir leurs pics d'activité (Noël, Saint-Valentin, mariages d'été).",
            },
        ],
        related_slugs: ["serveur", "maitre-hotel", "barman", "cuisinier"],
    },

    "maitre-hotel": {
        slug: "maitre-hotel",
        category_key: "maitre_hotel",
        emoji: "🎩",
        family: "Restauration & CHR",
        h1: "Maître d'hôtel — orchestrer le service en événementiel & gastronomie",
        title: "Maître d'hôtel : missions événementiel & gastronomie 22-30 €/h | Renforteo",
        meta_desc:
            "Missions de maître d'hôtel à Lyon, Grenoble, Valence : orchestration du service, mariages, gastronomie. 22-30 €/h en professionnel indépendant. Renforteo.",
        keywords: ["maître d'hôtel", "maitre hotel événementiel", "responsable de salle", "orchestration mariage", "maître hôtel gastronomique"],
        hero_img: "https://images.pexels.com/photos/6270541/pexels-photo-6270541.jpeg?auto=compress&cs=tinysrgb&w=1400",
        intro_paragraphs: [
            "Le maître d'hôtel est le chef d'orchestre du service. Il gère les brigades, dialogue avec le chef en cuisine, place les invités, orchestre les timings et fait respecter les standards de la maison. Sur un mariage ou un événement corporate, c'est lui qui garantit qu'à 20h45, le fromage est servi au bon moment et à la bonne température, sur toutes les tables simultanément.",
            "Sur Renforteo, les maîtres d'hôtel professionnels indépendants interviennent principalement sur des événements privés (mariages, cocktails haut de gamme, séminaires premium) dans la région Auvergne-Rhône-Alpes. Leur tarif horaire, supérieur à celui d'un chef de rang, reflète leur responsabilité opérationnelle et leur capacité à décharger totalement le client organisationnel.",
        ],
        client_pitch:
            "Organisez un mariage, un séminaire premium ou un dîner de gala et cherchez un maître d'hôtel pour orchestrer le service ? Recevez des CV vérifiés dans les 24 h.",
        candidate_pitch:
            "Vous êtes maître d'hôtel confirmé (3+ ans d'expérience et sens du leadership) ? Accédez à des missions événementielles rémunérées 22-30 €/h dans la région AURA.",
        salary_range: "22 – 30 €/h",
        salary_details: {
            min: 22,
            avg: 25,
            max: 30,
            factors: [
                "Restaurant bistronomique / séminaire corporate : 22-24 €/h",
                "Mariage haut de gamme / hôtel 4* : 24-26 €/h",
                "Gastronomique étoilé / hôtel 5* / gala privé : 26-30 €/h",
                "Missions VIP protection service : jusqu'à 32 €/h",
            ],
        },
        popular_cities: AURA_CITIES,
        typical_missions: [
            "Orchestration d'un mariage (100-300 couverts) : accueil, plan de salle, timings de service",
            "Dîner de gala corporate (soirée client, remise de prix, board dinner)",
            "Maître d'hôtel remplaçant en restaurant gastronomique (arrêt, congé)",
            "Séminaire haut de gamme avec dîner-cocktail dans un château (Château Perrache, Domaine des Buis…)",
            "Cocktail d'ouverture d'une saison en station de ski avec service à la française",
        ],
        prerequisites: [
            "Expérience minimum 3 ans en encadrement de service gastronomique",
            "Sens du leadership et capacité à briefer une équipe de 4 à 15 personnes",
            "Maîtrise complète du protocol de service à la française",
            "Anglais professionnel courant, autres langues appréciées (italien, japonais)",
            "SIRET professionnel indépendant + KBIS ou fiche INSEE + tenue complète (frac / smoking selon événement)",
        ],
        job_context:
            "La région AURA compte plusieurs pôles événementiels premium : les mariages viticoles autour de Valence (Crozes-Hermitage, Saint-Joseph), les événements corporate lyonnais (Cité Internationale, Palais de la Bourse), les séminaires tech grenoblois au Palais des Congrès Alpes Congrès, et les dîners d'ouverture de saison en station.",
        pros_cons: {
            pros: [
                "Tarif horaire le plus élevé de la restauration classique (jusqu'à 30 €/h)",
                "Rôle valorisant de chef d'orchestre",
                "Événements souvent prestigieux et enrichissants pour le CV",
            ],
            cons: [
                "Expérience 3+ ans exigée",
                "Responsabilité opérationnelle importante (gestion d'équipe)",
                "Standing du client scruté (tenue, gestuelle, présentation impeccable)",
            ],
        },
        faqs: [
            {
                q: "Faut-il fournir sa propre tenue de maître d'hôtel ?",
                a: "Oui pour la majorité des missions : costume noir 2 pièces, chemise blanche, cravate noire, chaussures noires cirées, gants blancs pour service à la française. Pour les événements grand luxe (dîner d'État, gala 500+ couverts), le smoking ou le frac peut être exigé — c'est précisé dans le brief.",
            },
            {
                q: "Quelle est la différence avec un chef de rang ?",
                a: "Le chef de rang gère un rang (15-25 couverts) et 2-4 commis. Le maître d'hôtel gère l'ensemble du service (50-500 couverts) et supervise plusieurs chefs de rang. Sa mission est stratégique (plans de salle, timings, briefing équipe) et non exécution.",
            },
            {
                q: "Combien gagne un maître d'hôtel événementiel régulier ?",
                a: "En moyenne 2 500 à 4 500 € net par mois pour un maître d'hôtel actif sur 15-20 missions événementielles/mois en AURA. En saison haute (mai-juin-septembre-décembre), les revenus peuvent dépasser 5 000 €.",
            },
            {
                q: "Renforteo assure-t-il l'anonymat auprès de mon employeur actuel ?",
                a: "Oui. Votre profil Renforteo n'est visible que par les clients qui vous font une proposition. Aucune information ne remonte à votre employeur actuel, et vous êtes libre de refuser toute mission dans son établissement.",
            },
        ],
        related_slugs: ["chef-de-rang", "serveur", "barman"],
    },

    barman: {
        slug: "barman",
        category_key: "barman",
        emoji: "🍸",
        family: "Restauration & CHR",
        h1: "Barman / Barmaid — cocktails, mixologie & bars événementiels",
        title: "Barman freelance : missions cocktails événementiel 17-24 €/h | Renforteo",
        meta_desc:
            "Missions de barman / barmaid à Lyon, Grenoble, Valence : bars à cocktails, mariages, cocktails corporate. 17-24 €/h en professionnel indépendant. Renforteo.",
        keywords: ["barman freelance", "barman Lyon", "barman mariage", "mixologie", "barman événementiel", "cocktail bar service"],
        hero_img: "https://images.pexels.com/photos/5379219/pexels-photo-5379219.jpeg?auto=compress&cs=tinysrgb&w=1400",
        intro_paragraphs: [
            "Le métier de barman associe technique (shaker, mixologie), créativité (cocktails signature) et sens du contact (bavardage avec les clients au comptoir). Renforteo recrute des barmen indépendants pour bars à cocktails, hôtels haut de gamme, cocktails événementiels et mariages sur toute la région Auvergne-Rhône-Alpes.",
            "Lyon dispose d'une scène cocktail parmi les plus dynamiques de France : plusieurs bars figurent régulièrement dans le classement World's 50 Best Bars (Le Fantôme, La Marquette, Bunny Bar). Grenoble et Valence développent également une culture cocktail portée par les afterworks corporate et les mariages viticoles.",
        ],
        client_pitch:
            "Vous organisez un cocktail d'entreprise, un mariage ou vous manquez d'un barman ce soir dans votre bar ? Recevez des candidatures de barmen vérifiés dans l'heure.",
        candidate_pitch:
            "Vous êtes barman expérimenté ou mixologue passionné ? Trouvez des missions rémunérées 17-24 €/h dans les bars et sur les événements haut de gamme de la région.",
        salary_range: "17 – 24 €/h",
        salary_details: {
            min: 17,
            avg: 20,
            max: 24,
            factors: [
                "Bar de brasserie / service standard : 17-19 €/h",
                "Bar à cocktails classique / mariage : 19-21 €/h",
                "Mixologue créatif / cocktail signature : 21-24 €/h",
                "Kit personnel apporté (shaker, jigger, muddler) : +2 €/h",
            ],
        },
        popular_cities: AURA_CITIES,
        typical_missions: [
            "Service bar en soirée mariage (open bar, cocktails signature)",
            "Cocktail d'ouverture d'entreprise (produits alcoolisés en vin, spritz, mocktails)",
            "Renfort bar en discothèque du samedi soir ou soirée rooftop",
            "Bar volant lors d'un salon (Sirha, Alpexpo) avec dégustations sponsorisées",
            "Extra bar dans hôtel 4-5* pour cocktail d'accueil corporate",
        ],
        prerequisites: [
            "1 an minimum d'expérience derrière un bar ou formation bartending",
            "Connaissance des grands classiques (Old Fashioned, Negroni, Espresso Martini…)",
            "Kit de mixologie personnel (shaker, jigger, doseur, verre à mélange)",
            "Sens du service et gestion du volume (cocktails à la chaîne en heure de rush)",
            "SIRET professionnel indépendant + KBIS ou fiche INSEE + RIB",
        ],
        job_context:
            "Le marché du cocktail événementiel en AURA a doublé entre 2020 et 2025 : les mariages exigent de plus en plus des bars à cocktails plutôt que du vin en libre-service, les entreprises multiplient les afterworks avec bar animé, et les rooftops lyonnais sont devenus de véritables laboratoires de mixologie.",
        pros_cons: {
            pros: [
                "Métier créatif, valorisant, avec fort contact client",
                "Missions événementielles souvent en soirée / week-end (compatible autre activité)",
                "Tarifs premium pour les mixologues créatifs",
            ],
            cons: [
                "Kit personnel à investir (150-300 €)",
                "Rythme intense sur cocktail dînatoire (100+ verres/heure)",
                "Fin de soirée tardive habituellement",
            ],
        },
        faqs: [
            {
                q: "Faut-il apporter son propre matériel ?",
                a: "Cela dépend de la mission. Pour un bar existant (hôtel, restaurant), le matériel est fourni. Pour un cocktail événementiel avec bar volant, un kit personnel (shaker, jigger, muddler, verre à mélange) est apprécié et donne accès à des tarifs légèrement supérieurs (+2 €/h).",
            },
            {
                q: "Renforteo travaille-t-il avec des bars à cocktails classés Top 50 mondial ?",
                a: "Oui, plusieurs bars lyonnais figurant dans les classements internationaux (World's 50 Best Bars, Best Bars France) passent régulièrement par Renforteo pour couvrir les samedis soirs et les événements privés.",
            },
            {
                q: "Puis-je proposer mes propres cocktails signature ?",
                a: "Oui, notamment sur les missions événementielles (mariages, cocktails corporate). Cela peut être un critère de sélection : indiquez dans votre profil vos 3-5 créations signature avec ingrédients et méthode. Les clients peuvent alors demander votre carte.",
            },
            {
                q: "Puis-je servir de l'alcool sans permis d'exploitation ?",
                a: "Oui, tant que vous intervenez dans les locaux du client (qui, lui, doit avoir la licence adéquate). Le permis d'exploitation n'est requis que si vous êtes l'exploitant de l'établissement — ce n'est pas votre cas en tant que prestataire.",
            },
        ],
        related_slugs: ["serveur", "chef-de-rang", "maitre-hotel"],
    },

    cuisinier: {
        slug: "cuisinier",
        category_key: "cuisinier",
        emoji: "👨‍🍳",
        family: "Restauration & CHR",
        h1: "Cuisinier — missions restaurants, traiteurs & événementiel gastronomique",
        title: "Cuisinier freelance : missions restaurants & traiteurs 18-25 €/h | Renforteo",
        meta_desc:
            "Missions de cuisinier à Lyon, Grenoble, Valence : brigade restaurant, cuisines de traiteur, événementiel. 18-25 €/h en professionnel indépendant. HACCP recommandée. Renforteo.",
        keywords: ["cuisinier freelance", "cuisinier Lyon", "chef brigade", "cuisinier traiteur", "HACCP cuisinier"],
        hero_img: "https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg?auto=compress&cs=tinysrgb&w=1400",
        intro_paragraphs: [
            "Le métier de cuisinier (aussi appelé « cook » ou « second de cuisine » en gastronomie) couvre un spectre large : entremétier, saucier, poissonnier, garde-manger, pâtissier. Renforteo met en relation restaurants, brasseries, traiteurs et hôtels de la région AURA avec des cuisiniers professionnels indépendants qualifiés — pour renforts ponctuels, remplacements longs (congé maternité) ou extras événementiels.",
            "La demande est particulièrement forte à Lyon (capitale gastronomique, plus de 4 000 restaurants), sur les grands événements Sirha et Bocuse d'Or, et dans les cuisines de traiteurs autour de Valence et Grenoble (mariages, séminaires d'entreprise). Les cuisiniers avec formation HACCP à jour et expérience étoilée accèdent aux missions premium.",
        ],
        client_pitch:
            "Coup dur en brigade, congé maternité à couvrir, ou traiteur qui prépare un mariage 500 couverts ? Un cuisinier qualifié pré-vérifié en moins de 24 h.",
        candidate_pitch:
            "Vous êtes cuisinier CAP / BEP / BTS avec 1 an d'expérience et votre formation HACCP à jour ? Trouvez des missions 18-25 €/h dans la région AURA.",
        salary_range: "18 – 25 €/h",
        salary_details: {
            min: 18,
            avg: 20,
            max: 25,
            factors: [
                "Commis renforcé / cuisinier junior (CAP + 1 an expérience) : 18-20 €/h",
                "Cuisinier confirmé (BTS + 3 ans, HACCP à jour) : 20-22 €/h",
                "Chef de partie / sous-chef gastronomique : 22-25 €/h",
                "Missions traiteur événementiel : majoration +2 €/h le week-end",
            ],
        },
        popular_cities: AURA_CITIES,
        typical_missions: [
            "Renfort brigade restaurant midi/soir (bistrot, brasserie, gastronomique)",
            "Cuisinier événementiel chez un traiteur (mariages, séminaires 200-500 couverts)",
            "Remplacement congé maternité en cuisine centrale (école, restauration collective)",
            "Extra week-end en brasserie ou restaurant touristique en station",
            "Poste temporaire en cuisine d'hôtel 4-5* pendant la saison estivale",
        ],
        prerequisites: [
            "CAP / BEP Cuisine ou BTS Restauration + 1 an minimum d'expérience",
            "Formation Hygiène Alimentaire (OA hygiène / HACCP) — recommandée par la majorité des clients ; l'obligation d'hygiène reste à la charge du donneur d'ordre",
            "Résistance physique et mentale (chaleur, cadence)",
            "Sens du travail en brigade et respect de la hiérarchie",
            "SIRET professionnel indépendant + KBIS ou fiche INSEE + carnet de vaccination à jour",
        ],
        job_context:
            "Le tissu culinaire lyonnais + valentinois + grenoblois compte plus de 5 000 établissements avec besoins réguliers en renforts (arrêt maladie, congé maternité, événements). Les traiteurs de la région sont particulièrement demandeurs de cuisiniers indépendants pour les samedis (mariages) et les grands événements corporate (Sirha, salons Alpexpo).",
        pros_cons: {
            pros: [
                "Métier valorisant, créativité culinaire",
                "Missions événementielles bien rémunérées",
                "Accès à des maisons prestigieuses via Renforteo",
            ],
            cons: [
                "Conditions physiques dures (chaleur, cadence)",
                "Formation Hygiène Alimentaire (OA / HACCP) recommandée pour la plupart des clients — ~150 € en ligne",
                "Investissement dans un kit couteaux personnel (200-500 €)",
            ],
        },
        faqs: [
            {
                q: "La formation HACCP est-elle obligatoire pour un cuisinier indépendant ?",
                a: "La réglementation impose qu'au moins une personne de l'établissement de restauration commerciale ait suivi la formation Hygiène Alimentaire (14 h). L'obligation légale porte sur l'établissement (donneur d'ordre), pas systématiquement sur chaque intervenant indépendant. Cependant, en pratique, la majorité des clients traiteurs et restaurateurs lyonnais demandent l'attestation HACCP à jour — c'est un fort différenciateur. Coût : 150-250 € en ligne, 300-500 € en présentiel, valable indéfiniment (rappel conseillé tous les 5 ans).",
            },
            {
                q: "Puis-je faire des missions de traiteur en tant qu'professionnel indépendant ?",
                a: "Oui, tant que vous intervenez dans les cuisines du traiteur (qui, lui, a les agréments sanitaires). Vous n'avez pas le droit de préparer des repas à emporter chez vous et de les revendre sans agrément séparé.",
            },
            {
                q: "Faut-il apporter ses couteaux ?",
                a: "Oui, la plupart des cuisiniers indépendants ont leur propre kit (couteau de chef 20 cm, éminceur, filet de sole, économe). Les clients apprécient un cuisinier bien équipé — c'est un signal de professionnalisme.",
            },
            {
                q: "Renforteo travaille-t-il avec des étoilés Michelin en cuisine ?",
                a: "Oui, plusieurs maisons étoilées de la région passent par Renforteo pour recruter des extras en cuisine, notamment pendant Noël, Saint-Valentin et la saison des mariages (mai-septembre).",
            },
        ],
        related_slugs: ["commis", "chef-de-rang", "plongeur"],
    },

    commis: {
        slug: "commis",
        category_key: "commis",
        emoji: "🥕",
        family: "Restauration & CHR",
        h1: "Commis de cuisine — première marche vers le métier de chef",
        title: "Commis de cuisine : missions dès 15 €/h en apprentissage | Renforteo",
        meta_desc:
            "Missions de commis de cuisine à Lyon, Grenoble, Valence : brigades restaurant, traiteurs événementiels. 15-18 €/h. Idéal débutant motivé. Renforteo.",
        keywords: ["commis cuisine", "commis Lyon", "débutant restauration", "brigade restaurant", "commis événementiel"],
        hero_img: "https://images.pexels.com/photos/2544829/pexels-photo-2544829.jpeg?auto=compress&cs=tinysrgb&w=1400",
        intro_paragraphs: [
            "Le commis de cuisine est la première marche du métier de chef. Sous la responsabilité d'un chef de partie ou d'un cuisinier confirmé, il assure la mise en place (éplucher, tailler, portionner), le nettoyage du poste, et l'assistance au dressage pendant le service. C'est un métier physique, formateur et souvent le vrai apprentissage terrain de la cuisine française.",
            "Sur Renforteo, les commis de cuisine professionnels indépendants trouvent des missions ponctuelles dans les brigades de restaurants, les cuisines de traiteurs événementiels et les cuisines d'hôtels de la région Auvergne-Rhône-Alpes. C'est aussi un excellent moyen pour un cuisinier débutant de découvrir plusieurs styles de cuisine (brasserie, gastro, traiteur, école hôtelière).",
        ],
        client_pitch:
            "Besoin d'un commis motivé pour renforcer votre brigade ce week-end ou couvrir un pic d'activité ? Recevez des candidatures en moins de 12 h.",
        candidate_pitch:
            "Vous démarrez dans la cuisine ou changez de vie ? Trouvez des missions de commis 15-18 €/h dans des brigades qualifiées, sans engagement, dans votre ville.",
        salary_range: "15 – 18 €/h",
        salary_details: {
            min: 15,
            avg: 16,
            max: 18,
            factors: [
                "Commis débutant (CAP ou 6 mois d'expérience) : 15-16 €/h",
                "Commis confirmé (1 an d'expérience) : 16-17 €/h",
                "Commis événementiel traiteur (cocktail, mariage) : 17-18 €/h",
                "Majorations : nuit +25 %, dimanche +50 %",
            ],
        },
        popular_cities: AURA_CITIES,
        typical_missions: [
            "Mise en place matinale en restaurant (éplucher, tailler, portionner)",
            "Renfort brigade traiteur pour un mariage ou séminaire événementiel",
            "Nettoyage du poste et rangement des chambres froides",
            "Assistance au dressage pendant le service",
            "Extra week-end en brasserie touristique (station de ski, viticole)",
        ],
        prerequisites: [
            "CAP Cuisine idéalement, ou 6 mois minimum d'expérience terrain",
            "Sens du travail en brigade et respect strict de la hiérarchie",
            "Résistance physique (station debout longue, cadence)",
            "Ponctualité extrême",
            "SIRET professionnel indépendant + KBIS ou fiche INSEE + RIB",
        ],
        job_context:
            "Les brigades AURA (Lyon, Grenoble, Valence) manquent chroniquement de commis. C'est pour de nombreux cuisiniers un point d'entrée idéal via l'auto-entreprise : vous découvrez plusieurs cuisines, vous vous constituez un réseau, et vous montez rapidement en compétence — les meilleurs commis passent chef de partie en 12-24 mois.",
        pros_cons: {
            pros: [
                "Métier d'entrée accessible (peu d'expérience requise)",
                "Apprentissage terrain rapide et enrichissant",
                "Passerelle naturelle vers cuisinier / chef de partie",
            ],
            cons: [
                "Rémunération la plus basse de la restauration",
                "Physiquement exigeant",
                "Hiérarchie stricte en brigade",
            ],
        },
        faqs: [
            {
                q: "Peut-on être commis sans diplôme ?",
                a: "Oui, si vous avez 6 mois minimum d'expérience terrain (extras, apprentissage, stages). Le CAP Cuisine reste apprécié et permet d'accéder aux missions premium et aux brigades gastronomiques.",
            },
            {
                q: "Quel avenir avec le métier de commis ?",
                a: "Un commis motivé et talentueux monte à chef de partie en 12-24 mois (cuisinier confirmé rémunéré 20-22 €/h). En 3-5 ans, il peut viser second de cuisine ou sous-chef. Renforteo affiche vos missions terminées et votre note — utile pour valoriser votre progression.",
            },
            {
                q: "Puis-je enchaîner plusieurs missions dans la même semaine ?",
                a: "Oui. La plupart des commis Renforteo actifs enchaînent 4-6 missions par semaine (mercredi-samedi soir + brunch dominical + traiteur événementiel), soit environ 30-35 h de mission facturée par semaine.",
            },
            {
                q: "Le traiteur fournit-il la tenue ?",
                a: "Non habituellement. Il faut prévoir sa propre veste de cuisine (blanche), son tablier, son pantalon pied-de-poule et ses chaussures antidérapantes. Budget total : 100-150 €.",
            },
        ],
        related_slugs: ["cuisinier", "plongeur", "chef-de-rang"],
    },

    plongeur: {
        slug: "plongeur",
        category_key: "plongeur",
        emoji: "🍽️",
        family: "Restauration & CHR",
        h1: "Plongeur professionnel — restaurants, brasseries & traiteurs événementiels",
        title: "Plongeur professionnel restaurant / traiteur 16-21 €/h | Renforteo",
        meta_desc:
            "Plongeurs professionnels pour restaurants, brasseries, traiteurs et événementiels à Lyon, Grenoble, Valence. 16-21 €/h en professionnel indépendant. Renforteo.",
        keywords: ["plongeur professionnel", "plongeur traiteur", "plongeur événementiel", "plongeur Lyon", "plonge mariage"],
        hero_img: "https://images.pexels.com/photos/4252140/pexels-photo-4252140.jpeg?auto=compress&cs=tinysrgb&w=1400",
        intro_paragraphs: [
            "Le métier de plongeur ne se limite pas au restaurant : il joue un rôle critique en événementiel traiteur (mariages, cocktails, séminaires) où les volumes se comptent en centaines de couverts à traiter en flux continu. Sans plongeur fiable, un service événementiel s'écroule.",
            "Renforteo recrute des plongeurs professionnels indépendants pour restaurants, brasseries, hôtels et traiteurs sur Lyon, Grenoble, Valence et l'ensemble de la région Auvergne-Rhône-Alpes. Les traiteurs événementiels sont particulièrement demandeurs — leur volume peut passer de 0 à 800 couverts en 6 heures un samedi soir, et le plongeur est le pivot silencieux de la mission.",
        ],
        client_pitch:
            "Restaurant en coup de feu ou traiteur qui prépare un mariage 500 couverts ? Un plongeur professionnel pré-vérifié en 24 h avec devis instantané.",
        candidate_pitch:
            "Métier accessible sans diplôme. Missions ponctuelles idéales en complément d'activité pour professionnels indépendants — traiteurs, brasseries, événementiel.",
        salary_range: "16 – 21 €/h",
        salary_details: {
            min: 16,
            avg: 18,
            max: 21,
            factors: [
                "Séminaire standard / cocktail entreprise : 16-18 €/h",
                "Mariage / brasserie touristique : 17-19 €/h",
                "Mariage haut de gamme / événement 500+ couverts : 19-21 €/h",
                "Majorations : nuit +25 %, dimanche +50 %, jour férié +50 %",
            ],
        },
        popular_cities: AURA_CITIES,
        typical_missions: [
            "Plonge chez un traiteur événementiel (cocktails, mariages, séminaires)",
            "Plonge en salle et cuisine, service soir/midi",
            "Renfort brasserie ou banquet événementiel",
            "Nettoyage complet des cuisines fin de service",
            "Plonge pendant les pics saisonniers (Fête des Lumières, saison ski)",
        ],
        prerequisites: [
            "Bonne condition physique (station debout, chaleur, humidité)",
            "Ponctualité et esprit d'équipe",
            "SIRET professionnel indépendant + KBIS ou fiche INSEE + RIB",
        ],
        job_context:
            "Les traiteurs événementiels de la région AURA (300+ acteurs sur Lyon-Rhône seul) tournent grâce à leur brigade de plongeurs. Un mariage traiteur classique = 600 couverts = ~1 800 assiettes + verres + couverts à nettoyer sur 6 heures. Le plongeur est le maillon critique.",
        pros_cons: {
            pros: [
                "Métier accessible sans diplôme",
                "Missions ponctuelles idéales en complément (soirées, week-ends)",
                "Cadence claire, pas de responsabilité opérationnelle",
            ],
            cons: [
                "Physiquement exigeant (station debout 6-10 h, chaleur, humidité)",
                "Peu valorisé socialement (rémunération correcte mais métier discret)",
                "Rythme très intense en pic (fin de service)",
            ],
        },
        faqs: [
            {
                q: "Combien gagne un plongeur traiteur en professionnel indépendant ?",
                a: "Entre 16 et 21 €/h HT selon l'événement, avec majorations nuit (+25 %) et dimanche (+50 %). En moyenne, un plongeur régulier génère 800 à 1 500 € net par mois en complément d'activité.",
            },
            {
                q: "Faut-il un diplôme pour être plongeur ?",
                a: "Non, aucune formation n'est requise. Une bonne condition physique et la ponctualité suffisent pour démarrer. Une formation HACCP peut être un plus pour accéder aux missions premium.",
            },
            {
                q: "Quelle différence entre plongeur restaurant et plongeur traiteur ?",
                a: "Les traiteurs travaillent sur des événements ponctuels (mariages, cocktails, séminaires) avec des volumes plus importants (200 à 1 000 couverts). Les tarifs horaires sont donc supérieurs (16-21 €/h vs 15-17 €/h en restaurant classique).",
            },
            {
                q: "Puis-je enchaîner plusieurs missions le même week-end ?",
                a: "Oui, mais attention à la fatigue. Un plongeur peut réaliser 2-3 missions traiteur sur un week-end (samedi midi + samedi soir + dimanche brunch), soit 15-20 h facturées. Au-delà, le rendement chute.",
            },
        ],
        related_slugs: ["commis", "cuisinier", "manutentionnaire"],
    },

    // ================= HÔTELLERIE / ÉVÉNEMENTIEL =================
    "hotesse-accueil": {
        slug: "hotesse-accueil",
        category_key: "hote",
        emoji: "💁",
        family: "Hôtellerie & Événementiel",
        h1: "Hôte / Hôtesse d'accueil — salons, congrès & événements corporate",
        title: "Hôte / hôtesse d'accueil : missions salons & événements 14-20 €/h | Renforteo",
        meta_desc:
            "Missions d'hôte / hôtesse d'accueil à Lyon, Grenoble, Valence : salons pro (Sirha, Alpexpo), congrès, événements corporate. 14-20 €/h en professionnel indépendant.",
        keywords: ["hotesse accueil Lyon", "hôte accueil salon", "hotesse Sirha", "hôtesse événementiel", "accueil corporate"],
        hero_img: "https://images.pexels.com/photos/5905964/pexels-photo-5905964.jpeg?auto=compress&cs=tinysrgb&w=1400",
        intro_paragraphs: [
            "L'hôte ou l'hôtesse d'accueil est la première impression que vos invités reçoivent — le sourire à l'entrée, l'orientation calme dans les couloirs, la remise du badge, la gestion VIP. C'est un métier qui exige présentation soignée, sens du contact, patience et anglais courant (particulièrement en région lyonnaise, cité des salons internationaux).",
            "Renforteo met en relation les organisateurs de salons pro (Sirha, Pollutec, Alpexpo, Global Industrie), les lieux d'accueil corporate (hôtels 4-5*, centres de congrès), les mariages et les événements privés avec des hôtes et hôtesses d'accueil professionnels indépendants vérifiés partout en Auvergne-Rhône-Alpes.",
        ],
        client_pitch:
            "Vous organisez un salon, un congrès, un mariage ou une soirée corporate ? Recevez des hôtes et hôtesses d'accueil pré-vérifiés (présentation, langues) en moins de 24 h.",
        candidate_pitch:
            "Vous avez le sens de l'accueil, une belle présentation et un bon niveau d'anglais ? Trouvez des missions 14-20 €/h près de chez vous dans la région AURA.",
        salary_range: "14 – 20 €/h",
        salary_details: {
            min: 14,
            avg: 16,
            max: 20,
            factors: [
                "Accueil standard (mariage, ouverture magasin, standiste salon) : 14-16 €/h",
                "Accueil bilingue (anglais/italien) sur salon B2B : 16-18 €/h",
                "Accueil VIP haut de gamme (hôtel 5*, gala corporate) : 18-20 €/h",
                "Majorations : nuit +25 %, dimanche +50 %, jour férié +50 %",
            ],
        },
        popular_cities: AURA_CITIES,
        typical_missions: [
            "Accueil standistes Eurexpo (Sirha, Pollutec, Piscine Global, Global Industrie)",
            "Accueil et vestiaire lors d'un mariage haut de gamme",
            "Accueil VIP corporate en hôtel 4-5* (séminaire, board dinner)",
            "Ouverture de magasin retail (accueil clientèle, distribution goodies)",
            "Accueil TGV VIP en gare (Lyon Part-Dieu, Valence-TGV Rovaltain)",
            "Hôtesse de bord piste (courses hippiques, salons automobiles)",
        ],
        prerequisites: [
            "Présentation soignée exigée (tenue sombre, coiffure, maquillage professionnel)",
            "Anglais courant minimum B1-B2 (indispensable sur salons internationaux)",
            "Autres langues appréciées : italien, allemand, mandarin",
            "Ponctualité extrême et sens du contact",
            "SIRET professionnel indépendant + KBIS ou fiche INSEE + RIB",
        ],
        job_context:
            "Le calendrier événementiel AURA est particulièrement dense : Sirha (fév.-mars), Piscine Global (nov.), Pollutec (nov.), Global Industrie (avr.), Salon des Vignerons de Valence (oct.), Alpexpo Grenoble toute l'année. Les hôtesses bilingues bien notées travaillent 15-20 jours par mois pendant ces grandes semaines.",
        pros_cons: {
            pros: [
                "Métier valorisant, cadre agréable (salons, hôtels haut de gamme)",
                "Horaires souvent en journée (compatibles avec autre activité)",
                "Missions événementielles bien réparties dans l'année",
            ],
            cons: [
                "Station debout longue (6-10 h avec talons)",
                "Anglais requis pour missions premium",
                "Investissement dans une garde-robe professionnelle (400-800 €)",
            ],
        },
        faqs: [
            {
                q: "Faut-il obligatoirement parler anglais pour être hôtesse ?",
                a: "Non, mais un niveau B1-B2 débloque les missions les mieux rémunérées (salons B2B internationaux Sirha, Global Industrie). Une hôtesse monolingue française peut faire des missions mariages, ouvertures magasins et événements corporate franco-français.",
            },
            {
                q: "Renforteo impose-t-il une tenue standardisée ?",
                a: "Non, mais chaque mission a un dresscode précis dans le brief (tailleur noir strict, robe noire sobre, tenue business casual…). Il vous appartient de posséder au minimum : tailleur / robe noire, chaussures fermées, foulard neutre.",
            },
            {
                q: "Puis-je faire hôtesse à Grenoble depuis Lyon ?",
                a: "Oui, si votre rayon de mobilité déclaré couvre 1h30 de route. Les missions Grenoble sont souvent en journée pleine (7h-19h) avec repas fourni, permettant l'aller-retour depuis Lyon.",
            },
            {
                q: "Quelle est la meilleure période pour démarrer ?",
                a: "Le calendrier événementiel AURA est fort de mars à juin (Sirha, mariages débutant), et de septembre à décembre (Pollutec, saison des salons, événements corporate fin d'année). Inscrivez-vous fin janvier pour être prêt pour le pic Sirha.",
            },
        ],
        related_slugs: ["serveur", "manutentionnaire"],
    },

    // ================= LOGISTIQUE / LIVRAISON =================
    livreur: {
        slug: "livreur",
        category_key: "livreur",
        emoji: "🚴",
        family: "Logistique & Livraison",
        h1: "Livreur / coursier — missions ponctuelles à Lyon, Grenoble, Valence",
        title: "Livreur & coursier : missions dès 16 €/h en professionnel indépendant | Renforteo",
        meta_desc:
            "Missions de livraison et coursier à Lyon, Grenoble, Valence : last-mile e-commerce, livraison événementielle, restauration. 16-22 €/h. Renforteo.",
        keywords: ["livreur Lyon", "coursier Lyon", "livraison last-mile", "livreur événementiel", "livreur professionnel indépendant"],
        hero_img: "https://images.pexels.com/photos/4392033/pexels-photo-4392033.jpeg?auto=compress&cs=tinysrgb&w=1400",
        intro_paragraphs: [
            "Le métier de livreur / coursier couvre plusieurs types de missions : last-mile e-commerce (paquets), livraison de repas événementiels (cocktails, séminaires), coursier boutiques et restaurants, renfort logistique en pic d'activité. À Lyon, Grenoble et Valence, la demande est portée par l'explosion du e-commerce, les grands événements corporate et le tourisme d'affaires.",
            "Sur Renforteo, les livreurs professionnels indépendants choisissent leurs missions à la journée, en régulier ou en temps plein. Vous facturez directement le client, les frais plateforme sont fixés à 8 % — soit 3 à 4 fois moins qu'une agence de coursiers traditionnelle. Pour la spécialité livraison-montage de meubles (Ikea, Conforama, But), voir notre page dédiée /metiers/livreur-monteur.",
        ],
        client_pitch:
            "Vous êtes une entreprise de logistique, un traiteur, une enseigne retail ou un e-commerce ? Publiez votre besoin de livreur : devis en 60 secondes.",
        candidate_pitch:
            "Vous avez un vélo, un scooter ou un utilitaire ? Trouvez des missions de livraison près de chez vous, choisissez vos créneaux, facturez directement.",
        salary_range: "16 – 22 €/h",
        salary_details: {
            min: 16,
            avg: 18,
            max: 22,
            factors: [
                "Livraison last-mile e-commerce (vélo/scooter) : 16-18 €/h",
                "Coursier vélo/scooter boutiques et restaurants : 17-19 €/h",
                "Livraison événementielle (repas cocktail, séminaire) : 19-22 €/h",
                "Frais kilométriques remboursés selon barème",
            ],
        },
        popular_cities: AURA_CITIES,
        typical_missions: [
            "Livraison de repas événementiels (cocktails Lyon Presqu'île, séminaires Grenoble Alpexpo)",
            "Coursier vélo/scooter pour boutiques et restaurants (Presqu'île, Confluence)",
            "Livraison last-mile e-commerce (paquets Amazon-like, retail local)",
            "Renfort logistique en pic d'activité (soldes, Fête des Lumières, saison ski)",
            "Livraison de fleurs, chocolats, vins (produits périssables ou fragiles)",
        ],
        prerequisites: [
            "Permis A/B ou vélo/trottinette selon la mission",
            "Ponctualité et bonne présentation",
            "Smartphone avec GPS et application client (parfois installée à la mission)",
            "SIRET professionnel indépendant + KBIS ou fiche INSEE + RIB",
        ],
        job_context:
            "Le e-commerce représente aujourd'hui 15 % du commerce français, et la région AURA est un hub logistique majeur (plates-formes Amazon Chalon, Lyon Vaise, Saint-Priest, Meyzieu). Les grands événements corporate lyonnais (Sirha, Pollutec) génèrent également d'énormes flux logistiques ponctuels (matériel, samples, retours).",
        pros_cons: {
            pros: [
                "Métier accessible avec ou sans véhicule motorisé",
                "Missions ponctuelles idéales en complément",
                "Facturation directe (pas d'intermédiaire de coursier classique)",
            ],
            cons: [
                "Physiquement exigeant (météo, station debout)",
                "Investissement véhicule + assurance transport si utilitaire",
                "Contraintes horaires strictes (créneaux de livraison chez le client)",
            ],
        },
        faqs: [
            {
                q: "Faut-il être professionnel indépendant pour livrer avec Renforteo ?",
                a: "Oui. Renforteo travaille exclusivement avec des professionnels indépendants / indépendants immatriculés (SIRET à 14 chiffres). Nous vous accompagnons gratuitement pour créer votre auto-entreprise en 10 min sur le site du gouvernement — écrivez-nous à contact@renforteo.fr.",
            },
            {
                q: "Puis-je livrer à vélo à Lyon ?",
                a: "Oui, les missions coursier à vélo sont particulièrement fréquentes en Presqu'île et Confluence (zones piétonnes ou à circulation limitée). Un bon vélo pliant ou un vélo électrique est un plus.",
            },
            {
                q: "Quels documents sont demandés à l'inscription ?",
                a: "Carte d'identité (recto + verso, vérifiée par IA), justificatif de domicile de moins de 3 mois, RIB, KBIS ou fiche INSEE. Pour la livraison de plus de 3,5 t : capacité de transport léger de marchandises.",
            },
            {
                q: "Renforteo prend-il en charge le carburant / véhicule ?",
                a: "Non directement, mais les frais kilométriques sont facturés au client selon un barème kilométrique standard (0,50 €/km voiture, 0,25 €/km scooter). Ces frais sont automatiquement ajoutés à votre facture.",
            },
        ],
        related_slugs: ["livreur-monteur", "manutentionnaire"],
    },

    "livreur-monteur": {
        slug: "livreur-monteur",
        category_key: "livreur",
        emoji: "🛠️",
        family: "Logistique & Livraison",
        h1: "Livreur-monteur de meubles — livraison + installation dès 18 €/h",
        title: "Livreur monteur de meubles : missions Ikea, Conforama 18-24 €/h | Renforteo",
        meta_desc:
            "Missions de livreur-monteur à Lyon, Grenoble, Valence : Ikea, Conforama, But, Maisons du Monde. Livraison + montage. 18-24 €/h en professionnel indépendant.",
        keywords: ["livreur monteur", "livreur monteur de meuble", "montage meubles Ikea", "livraison installation Conforama", "livreur monteur AURA"],
        hero_img: "https://images.pexels.com/photos/4491881/pexels-photo-4491881.jpeg?auto=compress&cs=tinysrgb&w=1400",
        intro_paragraphs: [
            "Le métier de livreur-monteur (aussi appelé « livreur monteur de meubles ») consiste à livrer ET installer un meuble chez le client final. Vous déballez, vous montez selon la notice, vous positionnez le meuble à l'endroit choisi et vous emportez les emballages. C'est un service premium très recherché par Ikea, Conforama, But, Maisons du Monde, Alinéa et les pure-players e-commerce d'ameublement.",
            "Sur Renforteo, les livreurs-monteurs professionnels indépendants interviennent partout en Auvergne-Rhône-Alpes (Lyon-Rhône, Grenoble-Isère, Valence-Drôme). Le tarif horaire moyen est supérieur au métier de livreur classique (18-24 €/h contre 16-20 €/h) car la mission demande outillage, expérience en assemblage et sens du service client.",
        ],
        client_pitch:
            "Vous êtes une enseigne d'ameublement, un artisan ou un e-commerçant ? Renforteo vous met en relation avec des livreurs-monteurs pré-vérifiés (SIRET, outillage confirmé) en moins de 24 h.",
        candidate_pitch:
            "Vous savez livrer, monter un meuble et laisser le client satisfait ? Missions livreur-monteur 18-24 €/h dans votre région, planning flexible, facturation directe.",
        salary_range: "18 – 24 €/h",
        salary_details: {
            min: 18,
            avg: 21,
            max: 24,
            factors: [
                "Meuble standard Ikea (~1h montage) : 18-20 €/h",
                "Meuble complexe Conforama / But (2-3 h montage) : 20-22 €/h",
                "Cuisine équipée / dressing sur-mesure (4-6 h) : 22-24 €/h",
                "Frais kilométriques remboursés",
            ],
        },
        popular_cities: AURA_CITIES,
        typical_missions: [
            "Livraison + montage de meubles Ikea, Conforama, But",
            "Livraison-installation e-commerce ameublement (Maisons du Monde, Alinéa)",
            "Livraison de cuisines équipées (montage sur place)",
            "Livraison-pose de literie et matelas grand format",
            "Montage de mobilier de bureau (open-space, hôtellerie)",
        ],
        prerequisites: [
            "Permis B et véhicule utilitaire (souvent fourni par le client)",
            "Outillage de base : visseuse, clés Allen, mètre, niveau",
            "Bonne condition physique (charges lourdes)",
            "Ponctualité, sens du service et présentation soignée",
            "SIRET professionnel indépendant + KBIS ou fiche INSEE + RIB",
        ],
        job_context:
            "Le e-commerce d'ameublement en France a doublé entre 2019 et 2024. Les grandes enseignes (Ikea, Conforama, But) sous-traitent massivement la livraison-installation à des indépendants — c'est l'une des rares niches où l'professionnel indépendant peut vraiment vivre à temps plein (400-700 € de CA par jour).",
        pros_cons: {
            pros: [
                "Tarifs élevés parmi les métiers logistiques",
                "Métier valorisant (contact client, résultat visible)",
                "Fort volume de missions (e-commerce d'ameublement en forte croissance)",
            ],
            cons: [
                "Investissement outillage (150-300 €) + parfois véhicule utilitaire",
                "Physiquement exigeant (charges lourdes, escaliers)",
                "Nécessite un vrai savoir-faire en bricolage",
            ],
        },
        faqs: [
            {
                q: "Quelle est la différence entre livreur et livreur-monteur ?",
                a: "Un livreur classique dépose le colis à la porte du client. Un livreur-monteur assure la livraison ET l'installation complète : déballage, montage selon la notice, mise en place du meuble à l'endroit choisi, et enlèvement des emballages. Le tarif horaire est donc supérieur (18-24 €/h contre 16-20 €/h en livraison simple).",
            },
            {
                q: "Faut-il de l'expérience pour être livreur-monteur ?",
                a: "Une expérience minimum en bricolage / assemblage de meubles est fortement recommandée. Les enseignes vérifient souvent votre profil avant validation. Renforteo affiche votre nombre de missions livreur-monteur terminées et votre note client sur votre profil.",
            },
            {
                q: "Est-ce que je dois fournir mon véhicule ?",
                a: "Cela dépend de la mission. Pour Ikea ou les grandes enseignes, un véhicule utilitaire est souvent fourni ou loué. Pour les artisans / e-commerce, votre propre véhicule (utilitaire type Kangoo, Traffic ou Master) est requis — les frais kilométriques sont alors remboursés.",
            },
            {
                q: "Puis-je enchaîner plusieurs missions dans la journée ?",
                a: "Oui. Un livreur-monteur expérimenté enchaîne en général 2 à 4 livraisons-montages par jour, selon la complexité (cuisine équipée = 1 seule, meuble Ikea standard = 3 à 5).",
            },
        ],
        related_slugs: ["livreur", "manutentionnaire"],
    },

    manutentionnaire: {
        slug: "manutentionnaire",
        category_key: "manutentionnaire",
        emoji: "📦",
        family: "Logistique & Livraison",
        h1: "Manutentionnaire — logistique événementielle & entrepôts",
        title: "Manutentionnaire freelance : missions logistique 15-19 €/h | Renforteo",
        meta_desc:
            "Missions de manutentionnaire à Lyon, Grenoble, Valence : logistique événementielle (Sirha, Alpexpo), entrepôts, montage stands. 15-19 €/h. Renforteo.",
        keywords: ["manutentionnaire Lyon", "manutention événementiel", "logistique salon", "manutention Sirha Alpexpo", "manutentionnaire entrepôt"],
        hero_img: "https://images.pexels.com/photos/6169668/pexels-photo-6169668.jpeg?auto=compress&cs=tinysrgb&w=1400",
        intro_paragraphs: [
            "Le métier de manutentionnaire recouvre la manipulation de charges (colis, palettes, mobilier événementiel) sur des chantiers logistiques : entrepôts e-commerce, montage-démontage de salons pro, dressage de mariages, préparation événementielle en salles de réception.",
            "En Auvergne-Rhône-Alpes, la demande est portée par les grands rendez-vous d'Eurexpo Lyon (Sirha, Pollutec, Piscine Global, Global Industrie) et d'Alpexpo Grenoble (Minalogic, Vitagora), qui mobilisent des centaines de manutentionnaires pour le montage-démontage des stands sur 2-4 jours consécutifs. Les traiteurs événementiels et les salles de réception recrutent également en régulier.",
        ],
        client_pitch:
            "Vous êtes un organisateur d'événements, un traiteur ou une entreprise logistique ? Recevez des manutentionnaires pré-vérifiés pour votre montage-démontage.",
        candidate_pitch:
            "Métier accessible sans diplôme, physique et valorisant. Trouvez des missions ponctuelles ou récurrentes 15-19 €/h dans la région AURA.",
        salary_range: "15 – 19 €/h",
        salary_details: {
            min: 15,
            avg: 16,
            max: 19,
            factors: [
                "Manutention standard entrepôt / traiteur : 15-16 €/h",
                "Montage-démontage salon pro (Sirha, Alpexpo) : 16-18 €/h",
                "Manutention lourde spécialisée (piano, décoration événementielle) : 18-19 €/h",
                "Majorations : nuit +25 %, dimanche +50 %, jour férié +50 %",
            ],
        },
        popular_cities: AURA_CITIES,
        typical_missions: [
            "Montage-démontage de stands sur salon pro (Sirha, Pollutec, Global Industrie)",
            "Manutention logistique en cuisine de traiteur (dressage mariage, cocktail)",
            "Renfort entrepôt e-commerce (préparation commandes, chargement palettes)",
            "Manutention spécialisée en décoration événementielle (mobilier, floral)",
            "Chargement / déchargement de camions pour agences de communication",
        ],
        prerequisites: [
            "Bonne condition physique (charges lourdes récurrentes)",
            "Ponctualité extrême (créneaux de montage-démontage stricts)",
            "Idéalement CACES 1/3/5 pour manutention avec chariots élévateurs",
            "Sens du travail en équipe",
            "SIRET professionnel indépendant + KBIS ou fiche INSEE + RIB",
        ],
        job_context:
            "Le salon Sirha à Eurexpo (fév.-mars) mobilise à lui seul 500+ manutentionnaires sur 4 jours de montage / 5 jours de démontage. Pollutec (nov.) et Global Industrie (avr.) sont dans la même échelle. C'est un métier saisonnier fort avec pics massifs et périodes creuses — idéal en complément d'une activité principale.",
        pros_cons: {
            pros: [
                "Métier accessible sans diplôme",
                "Missions concentrées (2-5 jours consécutifs) → facturation groupée",
                "CACES = tarif majoré",
            ],
            cons: [
                "Physiquement très exigeant",
                "Saisonnalité forte (pics + creux)",
                "Horaires étendus (montage la nuit, démontage à l'aube)",
            ],
        },
        faqs: [
            {
                q: "Faut-il un CACES pour être manutentionnaire ?",
                a: "Non pour la manutention manuelle. Oui pour la manutention avec chariots élévateurs (CACES 1/3/5). Le CACES 3 (chariot élévateur frontal) est le plus recherché — formation 350-500 € sur 3-5 jours, valable 5 ans.",
            },
            {
                q: "Quels sont les grands salons qui recrutent en manutention à Lyon ?",
                a: "Sirha (fév.-mars, salon mondial de la restauration), Pollutec (nov., environnement), Piscine Global (nov.), Global Industrie (avr.), Rehab Fair (juin) et Salon des Vignerons (oct.). Ces rendez-vous mobilisent chacun 200-500 manutentionnaires pour le montage-démontage.",
            },
            {
                q: "Combien gagne un manutentionnaire régulier en AURA ?",
                a: "En moyenne 1 200 à 2 000 € net par mois selon la fréquence. Un manutentionnaire actif sur les grands salons + missions traiteur en week-end peut générer 2 500 € net certains mois.",
            },
            {
                q: "Faut-il fournir des chaussures de sécurité ?",
                a: "Oui, presque toujours. Prévoyez chaussures de sécurité S3, gants de manutention et éventuellement une ceinture lombaire. Budget total : 100-200 € pour un kit complet.",
            },
        ],
        related_slugs: ["livreur", "livreur-monteur"],
    },

    // ================= ENTRETIEN / PROPRETÉ =================
    "femme-de-menage": {
        slug: "femme-de-menage",
        category_key: "femme_de_menage",
        emoji: "🧽",
        family: "Entretien & Propreté",
        h1: "Femme / homme de ménage — hôtels, bureaux & résidences",
        title: "Femme de ménage : missions hôtellerie & propreté 15-20 €/h | Renforteo",
        meta_desc:
            "Missions de femme / homme de ménage à Lyon, Grenoble, Valence : hôtels, bureaux, résidences, événementiel. 15-20 €/h en professionnel indépendant. Renforteo.",
        keywords: ["femme de ménage Lyon", "agent d'entretien", "ménage hôtel", "ménage bureaux", "propreté événementiel"],
        hero_img: "https://images.pexels.com/photos/4239146/pexels-photo-4239146.jpeg?auto=compress&cs=tinysrgb&w=1400",
        intro_paragraphs: [
            "Le métier d'agent d'entretien (souvent appelé femme ou homme de ménage) est un pilier discret mais indispensable de l'hôtellerie, de l'événementiel et de l'immobilier. Sur Renforteo, nous recrutons des professionnels professionnels indépendants pour l'entretien de chambres d'hôtel, de bureaux, de résidences seniors, ainsi que pour la remise en état post-événementiel (mariages, salons, séminaires).",
            "En Auvergne-Rhône-Alpes, la demande est particulièrement forte à Lyon (300+ hôtels, plusieurs milliers de bureaux Part-Dieu / Confluence), à Grenoble (résidences meublées, hôtels d'affaires) et à Valence (hôtellerie de tourisme, résidences seniors, propreté événementielle post-mariage viticole).",
        ],
        client_pitch:
            "Vous êtes un hôtel, un bureau, une résidence ou un organisateur d'événement ? Recevez des candidatures de professionnels de la propreté vérifiés en moins de 24 h.",
        candidate_pitch:
            "Vous êtes rigoureux, ponctuel et cherchez des missions de ménage 15-20 €/h près de chez vous ? Facturez directement, choisissez vos clients et vos créneaux.",
        salary_range: "15 – 20 €/h",
        salary_details: {
            min: 15,
            avg: 17,
            max: 20,
            factors: [
                "Ménage résidentiel / bureaux standard : 15-16 €/h",
                "Ménage hôtellerie 3-4* (chambres, parties communes) : 16-18 €/h",
                "Ménage hôtellerie 5* / remise en état post-événement : 18-20 €/h",
                "Majorations : dimanche +50 %, jour férié +50 %, urgence +10 %",
            ],
        },
        popular_cities: AURA_CITIES,
        typical_missions: [
            "Ménage quotidien de chambres d'hôtel (dressage lit, sanitaires, sol)",
            "Entretien de bureaux (Part-Dieu, Grenoble Presqu'île, zones tertiaires Valence)",
            "Remise en état post-événement (mariage, salon, séminaire)",
            "Ménage régulier chez le particulier (uniquement B2B via société de propreté)",
            "Grand ménage saisonnier d'ouverture / fermeture de résidence secondaire",
        ],
        prerequisites: [
            "Rigueur et sens du détail (protocole hôtellerie strict)",
            "Ponctualité (créneaux horaires serrés en hôtellerie)",
            "Résistance physique (station debout, gestes répétitifs)",
            "SIRET professionnel indépendant + KBIS ou fiche INSEE + RIB",
        ],
        job_context:
            "L'hôtellerie française tourne massivement grâce à des professionnels indépendants ou à des prestataires d'agences de propreté. Renforteo permet aux hôtels de sous-traiter à un professionnel directement, sans marge d'agence (30-50 %), ce qui rend le service à la fois plus rentable pour l'hôtelier et plus rémunérateur pour le prestataire.",
        pros_cons: {
            pros: [
                "Métier accessible sans diplôme",
                "Missions régulières possibles (ménage hôtel quotidien)",
                "Horaires modulables (matin uniquement, ou en journée)",
            ],
            cons: [
                "Physiquement exigeant (station debout, mouvements répétitifs)",
                "Peu de reconnaissance sociale",
                "Investissement dans un kit d'entretien personnel selon les clients (100-200 €)",
            ],
        },
        faqs: [
            {
                q: "Faut-il un diplôme pour être agent d'entretien ?",
                a: "Non, aucun diplôme n'est requis. Une formation CAP Agent de propreté et hygiène ou un TFP Agent de propreté sont un plus mais pas obligatoires. Les hôtels 5* apprécient une expérience préalable en hôtellerie.",
            },
            {
                q: "Renforteo propose-t-il des missions chez le particulier ?",
                a: "Non directement. Renforteo est une plateforme B2B — nous mettons uniquement en relation avec des entreprises (hôtels, bureaux, agences événementielles, résidences). Pour les missions chez le particulier, tournez-vous vers des plateformes CESU (Wecasa, Yoopies…).",
            },
            {
                q: "Combien gagne une femme de ménage en hôtellerie sur Renforteo ?",
                a: "Entre 15 et 20 €/h HT selon le standing. En hôtellerie 4-5*, un professionnel régulier peut générer 1 800 à 2 500 € net par mois, sans marge d'agence de propreté.",
            },
            {
                q: "Faut-il fournir ses propres produits d'entretien ?",
                a: "Non, en général. En hôtellerie et en bureaux, le client fournit les produits (chariot, aspirateur, produits d'entretien professionnels). Sur des missions événementielles ponctuelles, le kit peut être demandé — c'est précisé dans le brief.",
            },
        ],
        related_slugs: ["manutentionnaire", "plongeur"],
    },
};

export const JOB_SLUGS = Object.keys(JOB_SEO);

// Grouped for cross-linking blocks on other pages
export const JOBS_BY_FAMILY = {
    "Restauration & CHR": ["serveur", "chef-de-rang", "maitre-hotel", "barman", "cuisinier", "commis", "plongeur"],
    "Hôtellerie & Événementiel": ["hotesse-accueil"],
    "Logistique & Livraison": ["livreur", "livreur-monteur", "manutentionnaire"],
    "Entretien & Propreté": ["femme-de-menage"],
};
